/**
 * @fileoverview ClubOS Medical & Fitness dashboard — Team Doctor, Physiotherapist, Fitness Coach view
 */
/* eslint-disable react/prop-types */

import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftProgress from "components/SoftProgress";
import SoftButton from "components/SoftButton";
import ClickableCard from "components/ClickableCard";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import { medicalRecords, injuryHistory, squad } from "data/mockData";
import { formatDateIN, CLUB_NAME, SEASON } from "utils/indiaLocale";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const statusBadge = (s) =>
  ({ Cleared: "success", "Light Training": "info", Monitoring: "warning", "Gym Work": "secondary", Rehabilitation: "error" }[s] || "info");
const severityBadge = (s) =>
  ({ Mild: "success", Bruising: "info", "Grade 1": "warning", "Grade 2": "error" }[s] || "info");
const typeColor = (t) =>
  ({ Muscle: "error", Ligament: "warning", Contusion: "info" }[t] || "secondary");

// ─── Derived data ─────────────────────────────────────────────────────────────

const today = new Date();
function daysUntil(dateStr) { return Math.round((new Date(dateStr) - today) / 86400000); }

const activeInjuries = medicalRecords.filter((m) => m.status !== "Cleared");
const clearedThisMonth = medicalRecords.filter((m) => m.status === "Cleared");
const firstTeam = squad.filter((p) => p.squad === "First Team");
const fitPlayers = firstTeam.length - activeInjuries.filter((r) => firstTeam.some((p) => p.name === r.player)).length;
const returningIn14 = activeInjuries.filter((m) => { const d = daysUntil(m.expectedReturn); return d >= 0 && d <= 14; }).length;
const typeCounts = medicalRecords.reduce((acc, m) => { acc[m.injuryType] = (acc[m.injuryType] || 0) + 1; return acc; }, {});
const injuryTrendChart = {
  labels: injuryHistory.map((h) => h.month),
  datasets: [{ label: "Injuries per Month", color: "error", data: injuryHistory.map((h) => h.count) }],
};
const maxTypeCount = Math.max(...Object.values(typeCounts), 1);
const riskPlayers = squad.filter(
  (p) => p.age >= 32 || medicalRecords.some((m) => m.player === p.name && (m.status === "Rehabilitation" || m.status === "Monitoring"))
);
const fitFirstTeam = firstTeam.filter((p) => !activeInjuries.some((m) => m.player === p.name));
const hoverSx = { cursor: "pointer", borderRadius: "lg", transition: "transform 0.18s, box-shadow 0.18s", "&:hover": { transform: "translateY(-3px)", boxShadow: "0 10px 28px rgba(0,0,0,0.13)" } };

// ─── Sub-components ───────────────────────────────────────────────────────────

function InjuryRow({ m }) {
  const days = daysUntil(m.expectedReturn);
  const dc = days < 0 ? "error" : days < 7 ? "success" : days < 30 ? "warning" : "error";
  return (
    <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}><SoftTypography variant="button" fontWeight="medium">{m.flag} {m.player}</SoftTypography></td>
      <td style={{ padding: "8px 10px" }}><SoftTypography variant="caption" color="text">{m.position}</SoftTypography></td>
      <td style={{ padding: "8px 10px" }}><SoftTypography variant="caption" color="text">{m.injuryType}</SoftTypography></td>
      <td style={{ padding: "8px 10px" }}><SoftTypography variant="caption" color="text">{m.bodyPart}</SoftTypography></td>
      <td style={{ padding: "8px 10px" }}><SoftBadge badgeContent={m.severity} color={severityBadge(m.severity)} size="xs" container /></td>
      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}><SoftTypography variant="caption" color="text">{formatDateIN(m.injuryDate)}</SoftTypography></td>
      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}><SoftTypography variant="caption" color="text">{formatDateIN(m.expectedReturn)}</SoftTypography></td>
      <td style={{ padding: "8px 10px" }}><SoftTypography variant="caption" fontWeight="bold" color={dc}>{days < 0 ? "Overdue" : `${days}d`}</SoftTypography></td>
      <td style={{ padding: "8px 10px" }}><SoftBadge badgeContent={m.status} color={statusBadge(m.status)} size="xs" container /></td>
      <td style={{ padding: "8px 10px" }}>
        <Tooltip title={m.treatment} placement="top">
          <SoftTypography variant="caption" color="text" sx={{ maxWidth: 180, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "default" }}>{m.treatment}</SoftTypography>
        </Tooltip>
      </td>
    </tr>
  );
}

const TH = ({ cols }) => (
  <thead><tr>{cols.map((h) => <th key={h} style={{ textAlign: "left", padding: "6px 10px", borderBottom: "1px solid #e9ecef" }}><SoftTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">{h}</SoftTypography></th>)}</tr></thead>
);

// ─── Component ────────────────────────────────────────────────────────────────

function MedicalDashboard() {
  const [kpiModal, setKpiModal] = useState(null);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">Medical &amp; Fitness</SoftTypography>
          <SoftTypography variant="body2" color="text">{CLUB_NAME} · Medical Department · Season {SEASON}</SoftTypography>
        </SoftBox>

        {/* KPI cards */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            {[
              { key: "injuries", title: "Active Injuries", count: activeInjuries.length, pct: { color: "error", text: "requiring treatment" }, icon: { color: "error", component: "healing" } },
              { key: "cleared", title: "Cleared This Month", count: clearedThisMonth.length, pct: { color: "success", text: "returned to training" }, icon: { color: "success", component: "check_circle" } },
              { key: "returns", title: "Expected Returns", count: returningIn14, pct: { color: "info", text: "next 2 weeks" }, icon: { color: "info", component: "event" } },
              { key: "fitCount", title: "Fit to Play", count: fitPlayers, pct: { color: "success", text: `of ${firstTeam.length} registered` }, icon: { color: "success", component: "sports_soccer" } },
            ].map(({ key, title, count, pct, icon }) => (
              <Grid key={key} item xs={12} sm={6} lg={3}>
                <SoftBox onClick={() => setKpiModal(key)} sx={hoverSx}>
                  <MiniStatisticsCard title={{ text: title }} count={String(count)} percentage={pct} icon={icon} />
                </SoftBox>
              </Grid>
            ))}
          </Grid>
        </SoftBox>

        {/* Injury tracker */}
        <SoftBox mb={3}>
          <ClickableCard title="Current Injury Tracker" icon="healing" iconColor="error" tooltip="Click to expand all 10 columns per record"
            detailContent={
              <SoftBox sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <TH cols={["Player", "Pos", "Nationality", "Type", "Body Part", "Severity", "Injured", "Exp. Return", "Days Left", "Status", "Treatment", "Missed", "Notes"]} />
                  <tbody>
                    {medicalRecords.map((m) => {
                      const days = daysUntil(m.expectedReturn);
                      const dc = days < 0 ? "error" : days < 7 ? "success" : days < 30 ? "warning" : "error";
                      return (
                        <tr key={m.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}><SoftTypography variant="button" fontWeight="medium">{m.flag} {m.player}</SoftTypography></td>
                          {[m.position, m.nationality, m.injuryType, m.bodyPart].map((v) => <td key={v} style={{ padding: "8px 10px" }}><SoftTypography variant="caption" color="text">{v}</SoftTypography></td>)}
                          <td style={{ padding: "8px 10px" }}><SoftBadge badgeContent={m.severity} color={severityBadge(m.severity)} size="xs" container /></td>
                          <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}><SoftTypography variant="caption" color="text">{formatDateIN(m.injuryDate)}</SoftTypography></td>
                          <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}><SoftTypography variant="caption" color="text">{formatDateIN(m.expectedReturn)}</SoftTypography></td>
                          <td style={{ padding: "8px 10px" }}><SoftTypography variant="caption" fontWeight="bold" color={dc}>{days < 0 ? "Overdue" : `${days}d`}</SoftTypography></td>
                          <td style={{ padding: "8px 10px" }}><SoftBadge badgeContent={m.status} color={statusBadge(m.status)} size="xs" container /></td>
                          <td style={{ padding: "8px 10px", maxWidth: 200 }}><SoftTypography variant="caption" color="text">{m.treatment}</SoftTypography></td>
                          <td style={{ padding: "8px 10px" }}><SoftTypography variant="caption" color="text">{m.matchesMissed}</SoftTypography></td>
                          <td style={{ padding: "8px 10px", maxWidth: 260 }}><SoftTypography variant="caption" color="text">{m.notes}</SoftTypography></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </SoftBox>
            }>
            <SoftBox p={3}>
              <SoftTypography variant="h6" fontWeight="medium" mb={2}>Current Injury Tracker</SoftTypography>
              <SoftBox sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <TH cols={["Player", "Position", "Injury", "Body Part", "Severity", "Injured", "Exp. Return", "Days Left", "Status", "Treatment"]} />
                  <tbody>{medicalRecords.map((m) => <InjuryRow key={m.id} m={m} />)}</tbody>
                </table>
              </SoftBox>
            </SoftBox>
          </ClickableCard>
        </SoftBox>

        {/* Trend + Type + Risk */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              <ClickableCard title="Injury Trend" icon="show_chart" iconColor="error" tooltip="Click for month-by-month table with cumulative"
                detailContent={
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <TH cols={["Month", "Injuries", "Cumulative"]} />
                    <tbody>
                      {injuryHistory.reduce((acc, h) => { const prev = acc.length ? acc[acc.length - 1].cum : 0; acc.push({ ...h, cum: prev + h.count }); return acc; }, []).map((row) => (
                        <tr key={row.month} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "8px 10px" }}><SoftTypography variant="button" fontWeight="medium">{row.month}</SoftTypography></td>
                          <td style={{ padding: "8px 10px" }}><SoftTypography variant="caption" color="text">{row.count}</SoftTypography></td>
                          <td style={{ padding: "8px 10px" }}><SoftTypography variant="caption" color="text">{row.cum}</SoftTypography></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }>
                <GradientLineChart title="Injury Trend" description="Monthly injury count — current season" chart={injuryTrendChart} />
              </ClickableCard>
            </Grid>

            <Grid item xs={12} lg={4}>
              <ClickableCard title="Injury Breakdown by Type" icon="pie_chart" iconColor="warning" tooltip="Click to see all players per injury type"
                detailContent={
                  <SoftBox>
                    {Object.entries(typeCounts).map(([type, count]) => (
                      <SoftBox key={type} mb={2}>
                        <SoftBox display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Icon fontSize="small" color={typeColor(type)}>{type === "Muscle" ? "fitness_center" : type === "Ligament" ? "healing" : "compress"}</Icon>
                          <SoftTypography variant="button" fontWeight="bold">{type} — {count} case{count !== 1 ? "s" : ""}</SoftTypography>
                        </SoftBox>
                        {medicalRecords.filter((m) => m.injuryType === type).map((m) => (
                          <SoftBox key={m.id} ml={3} mb={0.5} display="flex" alignItems="center" gap={1}>
                            <SoftTypography variant="caption" fontWeight="medium">{m.flag} {m.player}</SoftTypography>
                            <SoftBadge badgeContent={m.bodyPart} color="secondary" size="xs" container />
                            <SoftBadge badgeContent={m.severity} color={severityBadge(m.severity)} size="xs" container />
                            <SoftBadge badgeContent={m.status} color={statusBadge(m.status)} size="xs" container />
                          </SoftBox>
                        ))}
                        <Divider sx={{ mt: 1 }} />
                      </SoftBox>
                    ))}
                  </SoftBox>
                }>
                <SoftBox p={3} sx={{ height: "100%" }}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>Injury Breakdown by Type</SoftTypography>
                  {Object.entries(typeCounts).map(([type, count]) => (
                    <SoftBox key={type} mb={2}>
                      <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <SoftBox display="flex" alignItems="center" gap={1}>
                          <Icon fontSize="small" color={typeColor(type)}>{type === "Muscle" ? "fitness_center" : type === "Ligament" ? "healing" : "compress"}</Icon>
                          <SoftTypography variant="button" fontWeight="medium">{type}</SoftTypography>
                        </SoftBox>
                        <SoftTypography variant="caption" color="text">{count} case{count !== 1 ? "s" : ""}</SoftTypography>
                      </SoftBox>
                      <SoftProgress value={Math.round((count / maxTypeCount) * 100)} color={typeColor(type)} variant="gradient" />
                    </SoftBox>
                  ))}
                </SoftBox>
              </ClickableCard>
            </Grid>

            <Grid item xs={12} lg={3}>
              <ClickableCard title="Risk Watch" icon="warning" iconColor="warning" tooltip="Click for full risk assessment per player"
                detailContent={
                  <SoftBox>
                    {riskPlayers.length === 0 && <SoftTypography variant="caption" color="text">No flagged players.</SoftTypography>}
                    {riskPlayers.map((p) => {
                      const injury = medicalRecords.find((m) => m.player === p.name);
                      const allInj = medicalRecords.filter((m) => m.player === p.name);
                      return (
                        <SoftBox key={p.id} mb={2} p={1.5} borderRadius="md" sx={{ border: "1px solid #e9ecef" }}>
                          <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                            <SoftTypography variant="button" fontWeight="bold">{p.flag} {p.name}</SoftTypography>
                            <SoftBadge badgeContent={injury ? "Injured" : "Age Risk"} color={injury ? "error" : "warning"} size="xs" container />
                          </SoftBox>
                          <SoftTypography variant="caption" color="text" display="block">Age: {p.age} · Position: {p.position} · {p.squad}</SoftTypography>
                          <SoftTypography variant="caption" color="text" display="block">Risk: {p.age >= 32 ? "Age-related high load risk" : "Active injury / under monitoring"}</SoftTypography>
                          <SoftTypography variant="caption" color="text" display="block">Injury history: {allInj.length} record{allInj.length !== 1 ? "s" : ""} this season</SoftTypography>
                          <SoftTypography variant="caption" color="text" display="block">
                            Recommendation: {injury ? `Status: ${injury.status}. ${injury.treatment}. Return: ${formatDateIN(injury.expectedReturn)}.` : "Monitor load carefully. Limit high-intensity sessions."}
                          </SoftTypography>
                        </SoftBox>
                      );
                    })}
                  </SoftBox>
                }>
                <SoftBox p={3} sx={{ height: "100%" }}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>Risk Watch</SoftTypography>
                  {riskPlayers.length === 0 && <SoftTypography variant="caption" color="text">No flagged players.</SoftTypography>}
                  {riskPlayers.map((p) => {
                    const injury = medicalRecords.find((m) => m.player === p.name);
                    return (
                      <SoftBox key={p.id} mb={1.5} display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="button" sx={{ fontSize: "1.1rem" }}>{p.flag}</SoftTypography>
                        <SoftBox flex={1}>
                          <SoftTypography variant="button" fontWeight="medium" display="block">{p.name}</SoftTypography>
                          <SoftTypography variant="caption" color="text">Age {p.age} · {injury ? injury.status : "Age — High load risk"}</SoftTypography>
                        </SoftBox>
                        <SoftBadge badgeContent={injury ? "Injured" : "Age"} color={injury ? "error" : "warning"} size="xs" container />
                      </SoftBox>
                    );
                  })}
                </SoftBox>
              </ClickableCard>
            </Grid>
          </Grid>
        </SoftBox>

        {/* Treatment Notes */}
        <SoftBox mb={3}>
          <SoftTypography variant="h6" fontWeight="medium" mb={2}>Treatment Notes</SoftTypography>
          <Grid container spacing={3}>
            {activeInjuries.map((m) => (
              <Grid key={m.id} item xs={12} lg={6}>
                <ClickableCard title={`${m.player} — Treatment Plan`} icon="medical_services" iconColor={statusBadge(m.status)} tooltip="Click for full treatment plan and doctor notes"
                  detailContent={
                    <SoftBox>
                      <SoftBox display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                        <SoftBadge badgeContent={m.injuryType} color={typeColor(m.injuryType)} size="xs" container />
                        <SoftBadge badgeContent={m.bodyPart} color="secondary" size="xs" container />
                        <SoftBadge badgeContent={m.severity} color={severityBadge(m.severity)} size="xs" container />
                        <SoftBadge badgeContent={m.status} color={statusBadge(m.status)} size="xs" container />
                      </SoftBox>
                      {[["Position", m.position], ["Nationality", m.nationality], ["Injury Date", formatDateIN(m.injuryDate)], ["Expected Return", formatDateIN(m.expectedReturn)], ["Days Remaining", daysUntil(m.expectedReturn) < 0 ? "Overdue" : `${daysUntil(m.expectedReturn)} days`], ["Matches Missed", `${m.matchesMissed} match${m.matchesMissed !== 1 ? "es" : ""}`]].map(([label, val]) => (
                        <SoftBox key={label} display="flex" gap={1} mb={0.5}>
                          <SoftTypography variant="caption" fontWeight="bold" color="dark" sx={{ minWidth: 130 }}>{label}:</SoftTypography>
                          <SoftTypography variant="caption" color="text">{val}</SoftTypography>
                        </SoftBox>
                      ))}
                      <Divider sx={{ my: 1.5 }} />
                      <SoftTypography variant="caption" fontWeight="bold" color="dark" display="block" mb={0.5}>Full Treatment Plan:</SoftTypography>
                      <SoftTypography variant="caption" color="text" display="block" mb={1.5}>{m.treatment}</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" color="dark" display="block" mb={0.5}>Doctor Notes:</SoftTypography>
                      <SoftBox p={1.5} borderRadius="md" sx={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
                        <SoftTypography variant="caption" color="text">{m.notes}</SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  }>
                  <SoftBox p={3}>
                    <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <SoftBox>
                        <SoftTypography variant="button" fontWeight="bold">{m.flag} {m.player}</SoftTypography>
                        <SoftTypography variant="caption" color="text" display="block">{m.position} · {m.nationality}</SoftTypography>
                      </SoftBox>
                      <SoftBadge badgeContent={m.status} color={statusBadge(m.status)} size="xs" container />
                    </SoftBox>
                    <Divider sx={{ my: 1 }} />
                    <SoftBox display="flex" gap={1} flexWrap="wrap" mb={1}>
                      <SoftBadge badgeContent={m.injuryType} color={typeColor(m.injuryType)} size="xs" container />
                      <SoftBadge badgeContent={m.bodyPart} color="secondary" size="xs" container />
                      <SoftBadge badgeContent={m.severity} color={severityBadge(m.severity)} size="xs" container />
                    </SoftBox>
                    <SoftBox mb={1}>
                      <SoftTypography variant="caption" fontWeight="bold" color="dark">Treatment Plan: </SoftTypography>
                      <SoftTypography variant="caption" color="text">{m.treatment}</SoftTypography>
                    </SoftBox>
                    <SoftBox mb={1}>
                      <SoftTypography variant="caption" fontWeight="bold" color="dark">Expected Return: </SoftTypography>
                      <SoftTypography variant="caption" color="text">{formatDateIN(m.expectedReturn)}</SoftTypography>
                      <SoftTypography variant="caption" color="text"> · {m.matchesMissed} match{m.matchesMissed !== 1 ? "es" : ""} missed</SoftTypography>
                    </SoftBox>
                    <SoftBox p={1.5} borderRadius="md" sx={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
                      <SoftBox display="flex" alignItems="flex-start" gap={0.5}>
                        <Icon sx={{ fontSize: "1rem", mt: 0.2, color: "#8392ab" }}>notes</Icon>
                        <SoftTypography variant="caption" color="text">{m.notes}</SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>
                </ClickableCard>
              </Grid>
            ))}
          </Grid>
        </SoftBox>

      </SoftBox>

      {/* KPI Modal */}
      <Dialog open={Boolean(kpiModal)} onClose={() => setKpiModal(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <SoftTypography variant="h6" fontWeight="bold" component="span">
            {{ injuries: "Active Injuries — Full Detail", cleared: "Cleared Players — Recovery Notes", returns: "Expected Returns — Next 14 Days", fitCount: "Fit First-Team Players" }[kpiModal]}
          </SoftTypography>
        </DialogTitle>
        <DialogContent>
          {(kpiModal === "injuries" ? activeInjuries : kpiModal === "cleared" ? clearedThisMonth : kpiModal === "returns" ? activeInjuries.filter((m) => { const d = daysUntil(m.expectedReturn); return d >= 0 && d <= 14; }) : []).map((m) => (
            <SoftBox key={m.id} mb={1.5} p={1.5} borderRadius="md" sx={{ border: "1px solid #e9ecef" }}>
              <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                <SoftTypography variant="button" fontWeight="bold">{m.flag} {m.player}</SoftTypography>
                <SoftBadge badgeContent={m.status} color={statusBadge(m.status)} size="xs" container />
              </SoftBox>
              <SoftTypography variant="caption" color="text" display="block">{m.position} · {m.nationality} · {m.injuryType} ({m.bodyPart}) · {m.severity}</SoftTypography>
              <SoftTypography variant="caption" color="text" display="block">Injured: {formatDateIN(m.injuryDate)} · Return: {formatDateIN(m.expectedReturn)} · Missed: {m.matchesMissed} matches</SoftTypography>
              <SoftTypography variant="caption" color="text" display="block">Treatment: {m.treatment}</SoftTypography>
              <SoftTypography variant="caption" color="text" display="block">Notes: {m.notes}</SoftTypography>
            </SoftBox>
          ))}
          {kpiModal === "fitCount" && fitFirstTeam.map((p) => (
            <SoftBox key={p.id} mb={0.5} display="flex" alignItems="center" gap={1}>
              <SoftTypography variant="caption">{p.flag} {p.name}</SoftTypography>
              <SoftTypography variant="caption" color="text">· {p.position} · Age {p.age}</SoftTypography>
            </SoftBox>
          ))}
        </DialogContent>
        <DialogActions>
          <SoftButton variant="gradient" color="dark" onClick={() => setKpiModal(null)}>Close</SoftButton>
        </DialogActions>
      </Dialog>

      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />
    </DashboardLayout>
  );
}

export default MedicalDashboard;
